import React, { useEffect, useRef, useState } from "react";
import { RenderCellContentProps } from "../../types";
import { Link } from "react-router-dom";
import ellipsize from "ellipsize";
import useDateFormatter from "hooks/formatDateWithPattern";
import { sentenceCase } from "change-case";
import useRoles from "hooks/roles";
import usePermissions from "hooks/usePermissions";
import useCustomBranding from "hooks/useCustomBranding";
import { Toasts } from "view/components/Toasts";
import CustomToolTip from "view/components/ToolTip";

export const RenderCellContent: React.FC<RenderCellContentProps> = ({
  columnDef,
  cellValue,
}) => {
  const { isInternalReviewerCommunityAdmin } = useRoles();
  const { communities } = usePermissions();
  const { formatDate } = useDateFormatter();
  const { getBrandPlaceholder } = useCustomBranding();
  const [isBrandsDropdownOpen, setIsBrandsDropdownOpen] = useState(false);
  const brandsDropdownRef = useRef<HTMLDivElement>(null);
  const placeholder = getBrandPlaceholder("listingProfile");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        brandsDropdownRef.current &&
        !brandsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsBrandsDropdownOpen(false);
      }
    };

    if (isBrandsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isBrandsDropdownOpen]);

  const showNoAccessToast = () =>
    Toasts.error(
      "You don’t have access to the page you’re trying to view. For more information, contact a system administrator."
    );

  const renderCommunityCell = (value: any) => {
    const canNavigate =
      value?.hasAccess &&
      value.accessibleForSuperAdmin &&
      communities.canViewViewCommunities &&
      isInternalReviewerCommunityAdmin();

    const content = (
      <div className="flex justify-start items-center relative w-[230px] group">
        <img
          src={value?.profileImage ? value?.profileImage : placeholder}
          alt=""
          height="100"
          width="100"
          className="object-cover mx-2 Img_user_Data"
          style={{ flexShrink: 0 }}
        />
        <div className="flex flex-col w-full justify-start items-start flex-wrap">
          <p className="text-sm w-[200px] text-left break-all text-textMid group-hover:text-primaryDark py-2">
            {`${value?.name}`}
          </p>
        </div>
      </div>
    );

    if (canNavigate) {
      return (
        <Link to={`/community/${value.id}/profile`} className="cursor-pointer">
          {content}
        </Link>
      );
    }

    return (
      <div
        className="flex justify-start items-center relative w-[230px] group cursor-pointer"
        onClick={showNoAccessToast}
      >
        {content}
      </div>
    );
  };

  const renderTextCell = (text: string) => (
    <div className="relative flex items-start flex-grow py-1 px-3 justify-start">
      <p className="text-sm text-left text-textMid whitespace-nowrap">
        {text}
      </p>
    </div>
  );

  const renderCommaSeparated = (value: any) =>
    renderTextCell(ellipsize(`${value}`, 50).split(",").join(", "));

  const renderBrands = (brands: any[]) => {
    const safeBrands = Array.isArray(brands) ? brands : [];
    const maxVisible = 3;
    const visibleBrands = safeBrands.slice(0, maxVisible);
    const hiddenBrands = safeBrands.slice(maxVisible);
    const hiddenCount = hiddenBrands.length;

    const getLogo = (brand: any) =>
      brand?.image?.thumb ||
      brand?.image?.path ||
      brand?.logo ||
      brand?.icon ||
      placeholder;

    return (
      <div className="relative" ref={brandsDropdownRef}>
        <div className="flex -space-x-2 w-[220px] py-1 px-2 flex-wrap items-center">
          {visibleBrands.map((brand: any, index: number) => {
            const zIndex = maxVisible - index;
            const name = brand?.name || brand?.title || "";
            return (
              <CustomToolTip
                key={`${name}-${index}`}
                title={name}
                placement="top"
              >
                <img
                  className="inline-block rounded-full h-10 w-10 object-cover"
                  height="40"
                  width="40"
                  src={getLogo(brand)}
                  alt={name}
                  style={{ zIndex }}
                />
              </CustomToolTip>
            );
          })}
          {hiddenCount > 0 && (
            <button
              className="py-1.5 px-3 text-[12px] bg-primaryExtraLight text-primary rounded-full hover:bg-primary/[0.08] transition"
              onClick={() => setIsBrandsDropdownOpen((prev) => !prev)}
              type="button"
            >
              +{hiddenCount}
            </button>
          )}
        </div>

        {isBrandsDropdownOpen && hiddenCount > 0 && (
          <div className="absolute top-full right-0 mt-3 w-[220px] bg-textWhite dark:bg-secondaryLight shadow-[0_0_6px_rgba(0,0,0,0.2)] rounded-xl p-2 z-10 max-h-[30vh] overflow-y-auto">
            {hiddenBrands.map((brand: any, index: number) => {
              const name = brand?.name || brand?.title || "";
              return (
                <div
                  key={`${name}-${index}`}
                  className="flex items-center gap-2 py-1 mb-1 last:mb-0 cursor-default"
                >
                  <img
                    className="inline-block rounded-full h-8 w-8 object-cover"
                    height="35"
                    width="35"
                    src={getLogo(brand)}
                    alt={name}
                  />
                  <span className="text-sm break-words text-textMid dark:text-caption capitalize">
                    {name || "-"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (
    typeof cellValue === "object" &&
    cellValue !== null &&
    columnDef.id === "communityName"
  ) {
    return renderCommunityCell(cellValue);
  }

  if (
    (cellValue !== null && columnDef.id === "organizations") ||
    columnDef.id === "communityAdmins"
  ) {
    return renderCommaSeparated(cellValue);
  }

  if (cellValue !== null && columnDef.id === "brands") {
    return renderBrands(cellValue);
  }

  if (cellValue !== null && columnDef.id === "dateAdded") {
    return renderTextCell(formatDate(cellValue?.toString()));
  }

  if (cellValue !== null && columnDef.id === "type") {
    return renderTextCell(sentenceCase(ellipsize(`${cellValue}`, 50)));
  }

  if (cellValue !== null) {
    return renderTextCell(ellipsize(`${cellValue}`, 50));
  }

  return null;
};
